import React, { useEffect, useState, useCallback } from 'react';
import { Loader2, Send, CalendarClock, X, Check, Sparkles, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import { fetchWithAuth } from './IntelligenceLayout';
import { useGraphStatus } from '../components/GraphMailboxStatus';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const postJSON = async (url, body) => {
  const token = localStorage.getItem('backoffice_token');
  const res = await fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
    body: JSON.stringify(body),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.detail || `Request failed (${res.status})`);
  return data;
};

const patchJSON = async (url, body) => {
  const token = localStorage.getItem('backoffice_token');
  const res = await fetch(url, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
    body: JSON.stringify(body),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.detail || `Request failed (${res.status})`);
  return data;
};

const presetTomorrow9am = () => {
  const d = new Date();
  d.setDate(d.getDate() + 1);
  d.setHours(9, 0, 0, 0);
  return d;
};

const DraftCard = ({ draft, onConsumed, onRejected, graphConnected }) => {
  const [subject, setSubject] = useState(draft.subject);
  const [body, setBody] = useState(draft.body);
  const [busy, setBusy] = useState(null); // 'sending' | 'scheduling' | 'rejecting'
  const [expanded, setExpanded] = useState(false);

  const markConsumed = async (status) => {
    try {
      await postJSON(`${API_URL}/api/crm/outreach-queue/${draft.id}/mark-consumed`, { status });
    } catch (e) { /* best-effort — the send/schedule already happened */ }
  };

  const saveEditsIfChanged = async () => {
    if (subject !== draft.subject || body !== draft.body) {
      await patchJSON(`${API_URL}/api/crm/outreach-queue/${draft.id}`, { subject, body });
    }
  };

  const sendNow = async () => {
    setBusy('sending');
    try {
      await saveEditsIfChanged();
      const data = await postJSON(`${API_URL}/api/crm/companies/${draft.company_id}/outreach-send`, {
        contact_id: draft.contact_id, subject, body, draft_type: draft.draft_type,
      });
      await markConsumed('approved_sent');
      toast.success(data.message || `Sent to ${draft.contact_email}`);
      onConsumed(draft.id);
    } catch (e) {
      toast.error(e.message || 'Send failed');
    } finally {
      setBusy(null);
    }
  };

  const scheduleNextBizDay = async () => {
    setBusy('scheduling');
    try {
      await saveEditsIfChanged();
      const at = presetTomorrow9am();
      const data = await postJSON(`${API_URL}/api/crm/companies/${draft.company_id}/outreach-schedule`, {
        contact_id: draft.contact_id, subject, body, draft_type: draft.draft_type,
        scheduled_at: at.toISOString(),
      });
      await markConsumed('approved_scheduled');
      toast.success(`Scheduled for ${at.toLocaleString()}`);
      onConsumed(draft.id);
    } catch (e) {
      toast.error(e.message || 'Could not schedule');
    } finally {
      setBusy(null);
    }
  };

  const reject = async () => {
    setBusy('rejecting');
    try {
      await postJSON(`${API_URL}/api/crm/outreach-queue/${draft.id}/reject`, {});
      onRejected(draft.id);
    } catch (e) {
      toast.error(e.message || 'Could not reject');
      setBusy(null);
    }
  };

  return (
    <div className="bg-white border border-gray-200 rounded-2xl p-5 space-y-3" data-testid={`outreach-queue-card-${draft.id}`}>
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="text-[#0A1F44] font-semibold text-base" data-testid="outreach-queue-company-name">{draft.company_name}</p>
          <p className="text-sm text-gray-500">{draft.contact_name || 'Unnamed contact'} · {draft.contact_email}</p>
        </div>
        <span className="inline-flex items-center gap-1 text-[11px] font-medium text-amber-700 bg-amber-50 border border-amber-200 rounded-full px-2 py-0.5 shrink-0">
          <Sparkles className="h-3 w-3" /> Auto-drafted
        </span>
      </div>

      <input
        value={subject}
        onChange={(e) => setSubject(e.target.value)}
        data-testid="outreach-queue-subject-input"
        className="w-full text-sm font-medium text-[#0A1F44] border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#0A1F44]/20"
      />
      <textarea
        value={body}
        onChange={(e) => setBody(e.target.value)}
        onFocus={() => setExpanded(true)}
        rows={expanded ? 10 : 4}
        data-testid="outreach-queue-body-textarea"
        className="w-full text-sm text-gray-700 border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#0A1F44]/20 whitespace-pre-wrap"
      />

      <div className="flex items-center gap-2 pt-1">
        <button
          onClick={sendNow}
          disabled={!!busy || !graphConnected}
          data-testid="outreach-queue-send-now-btn"
          className="inline-flex items-center gap-1.5 bg-[#0A1F44] text-white text-sm font-medium px-3 py-1.5 rounded-lg hover:bg-[#0A1F44]/90 disabled:opacity-50"
        >
          {busy === 'sending' ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />} Send Now
        </button>
        <button
          onClick={scheduleNextBizDay}
          disabled={!!busy || !graphConnected}
          data-testid="outreach-queue-schedule-btn"
          className="inline-flex items-center gap-1.5 bg-white border border-gray-300 text-[#0A1F44] text-sm font-medium px-3 py-1.5 rounded-lg hover:bg-gray-50 disabled:opacity-50"
        >
          {busy === 'scheduling' ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <CalendarClock className="h-3.5 w-3.5" />} Schedule (next business day 9am)
        </button>
        <button
          onClick={reject}
          disabled={!!busy}
          data-testid="outreach-queue-reject-btn"
          className="inline-flex items-center gap-1.5 text-gray-500 text-sm font-medium px-3 py-1.5 rounded-lg hover:bg-gray-50 disabled:opacity-50 ml-auto"
        >
          {busy === 'rejecting' ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <X className="h-3.5 w-3.5" />} Reject
        </button>
      </div>
      {!graphConnected && (
        <p className="text-xs text-red-600">Connect Microsoft mailbox (Companies tab) before sending or scheduling.</p>
      )}
    </div>
  );
};

const OutreachQueue = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const { status: graphStatus } = useGraphStatus();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await fetchWithAuth(`${API_URL}/api/crm/outreach-queue?status=pending_review`);
      setItems(data.items || []);
    } catch (e) {
      toast.error('Could not load outreach queue');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const removeItem = (id) => setItems((prev) => prev.filter((d) => d.id !== id));

  return (
    <div className="space-y-4" data-testid="outreach-queue-page">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold text-[#0A1F44]">Outreach Queue</h2>
          <p className="text-sm text-gray-500">
            Auto-drafted initial outreach for qualifying prospects — nothing here has been sent. Review, edit if needed, then Send Now or Schedule.
          </p>
        </div>
        <button onClick={load} data-testid="outreach-queue-refresh-btn" className="p-2 rounded-lg hover:bg-gray-100">
          <RefreshCw className="h-4 w-4 text-gray-500" />
        </button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-gray-400" /></div>
      ) : items.length === 0 ? (
        <div className="text-center py-16 text-gray-400 text-sm" data-testid="outreach-queue-empty-state">
          No drafts waiting for review. New qualifying prospects are drafted automatically once a day.
        </div>
      ) : (
        <div className="space-y-4">
          {items.map((draft) => (
            <DraftCard
              key={draft.id}
              draft={draft}
              graphConnected={graphStatus.connected}
              onConsumed={removeItem}
              onRejected={removeItem}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default OutreachQueue;
